import { Modal } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import styles from '../Style/FeaturesModal.module.scss';
import { Table } from './FeaturesComponents';
import { analyticsTable, preProcessedAnalyticsTable } from './FeaturesTables';

import dataReportImage from '../../../../../assets/reports/data_report.png';

import fweImage from '../../../../../assets/image/FWE.png';
import mapLayer from '../../../../../assets/image/map_layers.png';

import ReactPlayer from 'react-player';
import { Link, useNavigate } from 'react-router-dom';
import altimeterImage from '../../../../../assets/image/altimeter_comparison.png';

type PrivateProps = {
  visible: boolean;
  onHandleFeaturesModalCancel: () => void;
  handleCloseAllModals: () => void;
};

// currently not used
// const dataReportPDF =
//   process.env.PUBLIC_URL + '/static/media/data_report.0c3883b0db2eda6b39f4.pdf';

// const dataReportXLSX =
//   process.env.PUBLIC_URL +
//   '/static/media/data_report.fbab21f2c47f0e67bc1a.xlsx';

const links: { slug: string; text: string }[] = [
  { slug: 'introduction', text: 'Introduction' },
  { slug: 'recordings', text: 'Recordings' },
  { slug: 'validation', text: 'Validation' },
  { slug: 'analytics', text: 'Analytics' },
  { slug: 'mike-metocean-simulator', text: 'MIKE Metocean Simulator' },
  { slug: 'map-layers', text: 'Map Layers' },
  { slug: 'api', text: 'API Access' },
  { slug: 'data-report', text: 'Data Report' },
];

export default function FeaturesModal({
  visible,
  onHandleFeaturesModalCancel,
  handleCloseAllModals,
}: PrivateProps) {
  const navigate = useNavigate();
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  const [activeLink, setActiveLink] = useState(() => {
    const hash = location.hash.slice(1);
    return hash || links[0]?.slug || '';
  });

  const scrollToSection = useCallback((slug: string) => {
    const section = sectionRefs.current[slug];
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.warn(`Section with slug ${slug} not found`);
    }
  }, []);

  useEffect(() => {
    const hash = location.hash.slice(1);
    const initialSlug = hash || links[0]?.slug || '';

    if (visible && sectionRefs.current[initialSlug]) {
      scrollToSection(initialSlug);
    }
  }, [visible, scrollToSection]);

  return (
    <Modal
      centered
      footer={null}
      className={`featuresModal ${styles.featuresModal}`}
      title={[
        <div
          className={styles.modalTitle}
          key={'featuresModal-title-container'}
        >
          <h1>Get started</h1>
          <div>
            The portal offers a suit of features to access, visualize and
            process metocean data. The key features comprise
            <a href='#validation'> Validation</a>,
            <a href='#analytics'> Analytics</a>,
            <a href='#mike-metocean-simulator'> MIKE Metocean Simulator</a>,
            <a href='#map-layers'> Map Layers</a>,<a href='#api'> API Access</a>
            , and
            <a href='#data-report'> Data Report</a>.
          </div>
        </div>,
      ]}
      open={visible}
      onCancel={onHandleFeaturesModalCancel}
      // footer={[<div key='footerText'></div>]}
      width={'65vw'}
    >
      <div className={styles.featuresModalContent}>
        <div className={styles.listNavigation}>
          {links.map((link, index) => {
            return (
              <a
                key={`features-link-${index}`}
                className={`${activeLink === link.slug ? styles.active : ''}`}
                href={`#${link.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveLink(link.slug);
                  navigate(`#${link.slug}`, { replace: true });
                  scrollToSection(link.slug);
                }}
              >
                {link.text}
              </a>
            );
          })}
        </div>
        <div className={styles.content}>
          <section
            id='introduction'
            ref={(el) => (sectionRefs.current['introduction'] = el)}
          >
            <div className={styles.sectionContainer}>
              <Waypoint
                onPositionChange={(e) =>
                  e.currentPosition === 'inside' &&
                  setActiveLink('introduction')
                }
              />
              <h2>DHI MetOcean Data Portal</h2>
              <p>
                Enhance workflows with access to accurate hindcast metocean data
              </p>
              <p>
                <a
                  href='https://www.dhigroup.com/data-portals/metocean-data-portal'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  https://www.dhigroup.com/data-portals/metocean-data-portal
                </a>
              </p>
            </div>
          </section>
          <section
            id='recordings'
            ref={(el) => (sectionRefs.current['recordings'] = el)}
          >
            <div className={styles.sectionContainer}>
              <Waypoint
                onPositionChange={(e) =>
                  e.currentPosition === 'inside' && setActiveLink('recordings')
                }
              />
              <div className={styles.recordingItems}>
                <h2>Recordings</h2>
                <div className={styles.reactPlayer}>
                  <ReactPlayer
                    url='https://www.youtube.com/watch?v=19Ne0Uq0ze0'
                    controls={true}
                    width='100%'
                    height='100%'
                  />
                </div>
              </div>
              <div className={styles.recordingItems}>
                <h2>Validate</h2>
                <div className={styles.reactPlayer}>
                  <ReactPlayer
                    url='https://www.youtube.com/watch?v=MJj3DcSIaLA'
                    controls={true}
                    width='100%'
                    height='100%'
                  />
                </div>
              </div>
              <div className={styles.recordingItems}>
                <h2>MOOD Webinar 2022</h2>
                <div className={styles.reactPlayer}>
                  <ReactPlayer
                    url='https://www.youtube.com/watch?v=pduwYau5coM'
                    controls={true}
                    width='100%'
                    height='100%'
                  />
                </div>
              </div>
            </div>
          </section>
          <section
            id='validation'
            ref={(el) => (sectionRefs.current['validation'] = el)}
          >
            <div className={styles.sectionContainer}>
              <Waypoint
                onPositionChange={(e) =>
                  e.currentPosition === 'inside' && setActiveLink('validation')
                }
              >
                <h2>Validation</h2>
              </Waypoint>
              <p>
                Assuring the quality of the data is the first step to take.
                Therefore, validations can be freely assessed, prior to any
                purchase, via detailed comparisons against measurements.
              </p>
              <h3>Alimeter comparison</h3>
              <p>
                An altimeter comparison provides a quick assessment of the
                accuracy by comparing the local model data against satellite
                altimeter measurements within a radius of 50 km. This service is
                available globally (it is recommended to select a location {'>'}
                30 km from land).
              </p>
              <figure>
                <a href={altimeterImage} target={'_blank'} rel={'noreferrer'}>
                  <img
                    src={altimeterImage}
                    alt='Comparing significant wave height (Hm0) of the North Europe
                  dataset against satellite altimeter measurements within 50 km.'
                  />
                </a>
                <figcaption className='caption'>
                  Comparing significant wave height (Hm0) of the North Europe
                  dataset against satellite altimeter measurements within 50 km.
                </figcaption>
              </figure>
              <h3>In situ Comparison</h3>
              <p>{'[ In development ]'}</p>
            </div>
          </section>
          <section
            id='analytics'
            ref={(el) => (sectionRefs.current['analytics'] = el)}
          >
            <div className={styles.sectionContainer}>
              <Waypoint
                onPositionChange={(e) =>
                  e.currentPosition === 'inside' && setActiveLink('analytics')
                }
              >
                <h2>Analytics</h2>
              </Waypoint>
              <p>
                The analytics can be used to assess the metocean conditions
                anywhere in the world. It is free of charge to analyze the last
                year of data from any
                <span
                  className={styles.spanLink}
                  onClick={() => {
                    handleCloseAllModals();
                    navigate('metadata');
                  }}
                >
                  {' '}
                  dataset
                </span>
                . Holders of a valid
                <span
                  className={styles.spanLink}
                  onClick={() => {
                    handleCloseAllModals();
                    navigate('pricing#subscription');
                  }}
                >
                  {' '}
                  subscription{' '}
                </span>
                can freely analyze the full period of data (typically 40+
                years).
              </p>
              <h3>Interactive analytics</h3>
              <p>
                The interactive analytics are processed on the fly and can be
                configured by the user. An overview of the suite of interactive
                analytics is given below.
              </p>
              {/* TODO: UPDATE IN PROGRESS TEXT IN TABLE */}
              <Table
                th1='Interative analytic'
                th2='Example'
                tableObjects={analyticsTable}
                theme={'light'}
              />
              <h3>Pre-processed analytics</h3>
              <p>
                Pre-processed analytics comprise complex or custom analytics
                that are processed offline within designated areas for certain
                datasets and made available online. Examples of pre-processed
                analytics from the TNW datasets are given below.
              </p>
              <Table
                th1='Pre-processed analytic'
                th2='Example'
                tableObjects={preProcessedAnalyticsTable}
                theme={'light'}
              />
            </div>
          </section>
          <section
            id='mike-metocean-simulator'
            ref={(el) => (sectionRefs.current['mike-metocean-simulator'] = el)}
          >
            <div className={styles.sectionContainer}>
              <Waypoint
                onPositionChange={(e) =>
                  e.currentPosition === 'inside' &&
                  setActiveLink('mike-metocean-simulator')
                }
              >
                <h2>MIKE Metocean Simulator</h2>
              </Waypoint>
              <p>
                Do you need to generate even higher resolution global data? Meet
                MIKE Metocean Simulator (MS), a fast, easy and flexible
                cloud-based wave modelling tool that seamlessly integrates with
                MetOcean On Demand.
              </p>
              <p>
                At the heart of MIKE Metocean Simulator (MS) lies a powerful set
                of features designed to make your wave modelling experience
                fast, easy, and flexible. MIKE Metocean Simulator seamlessly
                integrates with the MetOcean On-Demand (MOOD) portal to
                streamline your workflow.
              </p>
              <br />
              <strong>Fast and simple setup</strong>
              <p>
                Begin using the software effortlessly with a user-friendly web
                based setup. The interface provides access to pre-defined
                high-quality MIKE 21 Spectral Wave templates. Intended for all
                users, including novices and seasoned professionals.
              </p>
              <br />
              <strong>Assisted meshing</strong>
              <p>
                Generate a semi-automatic mesh instantly or upload custom
                meshes, including those created with MIKE Mesh Builder.
              </p>
              <br />
              <strong>Flexible input data options</strong>
              <p>
                Automatically access over 40 years of continuous boundary
                conditions from DHI sources, or upload your own data. MIKE
                Metocean Simulator provides the flexibility you need.
              </p>
              <br />
              <strong>Cloud-powered simulation and storage</strong>
              <p>
                MIKE Metocean Simulator runs simulations on 44 core virtual
                computers in the cloud. The cloud-based platform supports
                collaborative efforts while streamlining the management of
                project data.
              </p>
              <br />
              <strong>Metocean On-Demand (MOOD)</strong>
              <p>
                Seamlessly push data to the MOOD portal for map-based
                visualisation and data sharing while gaining access to powerful
                analytics.
              </p>
              <br />
              <strong>Data validation in MOOD</strong>
              <p>
                Enhance data quality control by generating validation plots for
                selected data points
              </p>
              <br />
              <p>
                Visit the product page
                <a
                  href='https://www.mikepoweredbydhi.com/products/mike-cloud/mike-metocean-simulator'
                  target='_blank'
                  rel='noreferrer noopener'
                >
                  {' '}
                  link{' '}
                </a>
                to learn more about how you can benefit from this simple, yet
                powerful wave modelling solution.
              </p>
              <h3>Local Model Area</h3>
              <p>
                The Local Model Area highlights the coverage scope of the Fast
                Wave Emulator, delineated by a gradient of yellow to greenThis
                represents the boundaries within which the FWE calculations are
                performed.
              </p>
              <h3>Local Model Point</h3>
              <p>
                The Local Model Point represents the specific coordinate where
                the Fast Wave Emulator operates and performs its calculations.
                This point is seamlessly integrated with the FWE system on the
                Mike Cloud platform.
              </p>
              <br />
              <figure>
                <a href={fweImage} target={'_blank'} rel={'noreferrer'}>
                  <img
                    src={fweImage}
                    alt={`
                    The Local Model Area, delineated by a yellow polygon, indicates the MIKE MS model boundaries.
                    Within this area, the Local Model Points are represented by solid yellow dots, signifying
                    specific coordinates where data from MIKE MS is available.
                    `}
                  />
                </a>
                <figcaption className='caption'>
                  {`The Local Model Area, delineated by a yellow polygon, indicates the MIKE MS model boundaries.
                    Within this area, the Local Model Points are represented by solid yellow dots, signifying
                    specific coordinates where data from MIKE MS is available.`}
                </figcaption>
              </figure>
            </div>
          </section>
          <section
            id='map-layers'
            ref={(el) => (sectionRefs.current['map-layers'] = el)}
          >
            <div className={styles.sectionContainer}>
              <Waypoint
                onPositionChange={(e) =>
                  e.currentPosition === 'inside' && setActiveLink('map-layers')
                }
              >
                <h2>Map Layers</h2>
              </Waypoint>
              {/* TODO: ADD LINKS TO SPANS? */}
              <p>
                The map features customizable layers such as
                <a href='#shapeFileUpload'> shape file upload </a> and
                <a href='#surfaceMaps'> surface maps </a>.
              </p>
              <h3 id='shapeFileUpload'> Shape file upload </h3>
              <p>
                Upload custom shape files to display e.g., your project area or
                points of interest on the map. Multiple shape files may be added
                freely.
              </p>
              <h3 id='surfaceMaps'>Surface maps</h3>
              <p>
                Surface maps are zoomable 2D layers of various variables. The
                color legend automatically adjusts to the content of the map
                window. All datasets have water depth as a surface map, while
                some datasets have also other maps, such as e.g., mean H
                <sub>m0</sub>.
              </p>
              <figure>
                <a href={mapLayer} target={'_blank'} rel={'noreferrer'}>
                  <img
                    src={mapLayer}
                    alt={`Surface map "Water depth" of the North Europe dataset, overlain by shape file "OWF"s - Europe' (orange lines).`}
                  />
                </a>
                <figcaption className='caption'>
                  {`Surface map "Water depth" of the North Europe dataset, overlain by shape file "OWF"s - Europe' (orange lines).`}
                </figcaption>
              </figure>
            </div>
          </section>
          <section id='api' ref={(el) => (sectionRefs.current['api'] = el)}>
            <div className={styles.sectionContainer}>
              <Waypoint
                onPositionChange={(e) =>
                  e.currentPosition === 'inside' && setActiveLink('api')
                }
              >
                <h2>API Access</h2>
              </Waypoint>
              <p>
                The MOOD API facilitates download of time series data without
                going through the website interface.
              </p>
              <ul>
                <li>
                  <div
                    className={styles.spanLink}
                    onClick={() => {
                      navigate('/api');
                    }}
                  >
                    https://metocean-on-demand.com/api
                  </div>
                </li>
              </ul>
            </div>
          </section>
          <section
            id='data-report'
            ref={(el) => (sectionRefs.current['data-report'] = el)}
          >
            <div className={styles.sectionContainer}>
              <Waypoint
                onPositionChange={(e) =>
                  e.currentPosition === 'inside' && setActiveLink('data-report')
                }
              >
                <h2>Data Report</h2>
              </Waypoint>
              <p>
                A Metocean Assessment Data Report provides a fast, efficient,
                and economical starting point for any concept/basic/pre-FEED
                offshore design assessments.
                <Link to={'/contact'}> Contact</Link> our team for any
                inquiries.
              </p>
              <ul>
                <li>Semi-automated metocean data report</li>
                <li>Wind, waves, currents & water level</li>
                <li>Normal & extreme conditions</li>
                <li>Anywhere in the world</li>
                <li>Compliant with IEC and DNV standards</li>
                <li>Formats: PDF & Excel (.xlsx)</li>
                <li>Fast delivery (&lt; 5 days)</li>
              </ul>
              <img src={dataReportImage} width={960} alt='' />
            </div>
          </section>
        </div>
      </div>
    </Modal>
  );
}
